import React, { useState } from 'react';
import {
  CheckSquare,
  Mail,
  StickyNote,
  ClipboardList,
  Send,
  Check,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  CalendarPlus,
  Eye,
  Activity,
  UserPlus,
  FileText,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import PendingActionPreviewModal from '../pending/PendingActionPreviewModal';
import { useAuth } from '../../context/AuthContext';
import { PERM } from '../../constants/permissions';
import { AUTOMATION_ACTION_META } from '../../constants/automationActions';
import { canExecutePendingAction } from '../../utils/pendingActionPermissions';
import AutomationActionParams from '../pending/AutomationActionParams';
import AutomationOutcomeBanner from '../pending/AutomationOutcomeBanner';
import AutomationProgressDetails from '../pending/AutomationProgressDetails';
import { automationOutcome } from '../../utils/automationOutcome';

/**
 * Maps a PendingActionType to a human-readable label, icon, and colour.
 */
const ACTION_TYPE_META = {
  ...AUTOMATION_ACTION_META,
  create_task: { label: 'Create Task', icon: CheckSquare, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/30' },
  update_task: { label: 'Update Task', icon: ClipboardList, color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/30' },
  send_email: { label: 'Send Email', icon: Mail, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/30' },
  add_note: { label: 'Add Note', icon: StickyNote, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/30' },
  log_activity: { label: 'Log Activity', icon: Activity, color: 'text-cyan-600', bg: 'bg-cyan-50 dark:bg-cyan-900/30' },
  send_survey: { label: 'Send Survey', icon: Send, color: 'text-teal-600', bg: 'bg-teal-50 dark:bg-teal-900/30' },
  create_contact: { label: 'Create Contact', icon: UserPlus, color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/30' },
  create_meeting: { label: 'Create Meeting', icon: CalendarPlus, color: 'text-indigo-600', bg: 'bg-indigo-50 dark:bg-indigo-900/30' },
  generate_qbr: { label: 'Generate QBR', icon: FileText, color: 'text-rose-600', bg: 'bg-rose-50 dark:bg-rose-900/30' },
  refresh_health_score: { label: 'Refresh Health Score', icon: RefreshCw, color: 'text-lime-700', bg: 'bg-lime-50 dark:bg-lime-900/30' },
};

const STATUS_VARIANT = {
  pending: { label: 'Pending', variant: 'outline', className: 'border-amber-400 text-amber-700 dark:text-amber-400' },
  executing: { label: 'Executing', variant: 'secondary', className: 'text-blue-700 dark:text-blue-300' },
  approved: { label: 'Approved', variant: 'default', className: 'bg-green-600 text-white' },
  rejected: { label: 'Rejected', variant: 'destructive', className: '' },
  executed: { label: 'Executed', variant: 'default', className: 'bg-green-600 text-white' },
  failed: { label: 'Failed', variant: 'destructive', className: '' },
  execution_unknown: { label: 'Needs Review', variant: 'destructive', className: '' },
  expired: { label: 'Expired', variant: 'secondary', className: 'text-muted-foreground' },
};

/**
 * Render a concise summary of the action parameters.
 * Shows title, priority, due date for tasks (+ description),
 * and subject, to, body for emails.
 */
function ActionParamsSummary({ actionType, params, action }) {
  if (!params || Object.keys(params).length === 0) return null;
  if (AUTOMATION_ACTION_META[actionType]) {
    return <AutomationActionParams actionType={actionType} params={params} action={action} />;
  }

  const items = [];

  if (actionType === 'create_task' || actionType === 'update_task') {
    if (params.title) items.push({ k: 'Title', v: params.title });
    if (params.priority) items.push({ k: 'Priority', v: params.priority });
    if (params.due_date) items.push({ k: 'Due', v: new Date(params.due_date).toLocaleDateString() });
    if (params.description) items.push({ k: 'Description', v: params.description, multiline: true });
  } else if (actionType === 'send_email') {
    if (params.subject) items.push({ k: 'Subject', v: params.subject });
    if (params.to || params.contact_email) items.push({ k: 'To', v: params.to || params.contact_email });
    if (params.from_name || params.from_email) {
      const sender = params.from_name && params.from_email
        ? `${params.from_name} <${params.from_email}>`
        : params.from_name || params.from_email;
      items.push({ k: 'From', v: sender });
    }
    if (params.body) items.push({ k: 'Body', v: params.body, multiline: true });
  } else if (actionType === 'send_survey') {
    if (params.survey_name) items.push({ k: 'Survey', v: params.survey_name });
  } else if (actionType === 'add_note') {
    if (params.content) items.push({ k: 'Note', v: params.content, multiline: true });
  } else if (actionType === 'log_activity') {
    if (params.activity_type) items.push({ k: 'Type', v: params.activity_type.replace(/_/g, ' ') });
    if (params.summary) items.push({ k: 'Summary', v: params.summary, multiline: true });
    if (params.notes) items.push({ k: 'Notes', v: params.notes, multiline: true });
    if (params.activity_date) items.push({ k: 'Date', v: params.activity_date });
    if (params.duration_minutes !== null && params.duration_minutes !== undefined) {
      items.push({ k: 'Duration', v: `${params.duration_minutes} min` });
    }
  } else if (actionType === 'create_meeting') {
    if (params.title) items.push({ k: 'Title', v: params.title });
    if (params.meeting_type) items.push({ k: 'Type', v: params.meeting_type.replace(/_/g, ' ') });
    if (params.date) items.push({ k: 'Date', v: params.date });
    if (params.time) items.push({ k: 'Time', v: params.time });
    if (params.timezone) items.push({ k: 'Timezone', v: params.timezone });
    items.push({ k: 'Calendar', v: params.create_calendar_event ? 'Connected Calendar Requested' : 'Email Invitation Only' });
    if (params.attendees?.length) items.push({ k: 'Attendees', v: params.attendees.join(', '), multiline: true });
    if (params.duration) items.push({ k: 'Duration', v: `${params.duration} min` });
    if (params.conference_link) items.push({ k: 'Link', v: params.conference_link });
    if (params.conference_passcode) items.push({ k: 'Passcode', v: params.conference_passcode });
    if (params.notes) items.push({ k: 'Notes', v: params.notes, multiline: true });
  }

  if (items.length === 0) return null;

  return (
    <div className="mt-2 space-y-1">
      {items.map(({ k, v, multiline }) => (
        <div key={k} className={cn('flex text-xs', multiline && 'flex-col gap-0.5')}>
          <span className={cn('text-muted-foreground shrink-0', multiline ? '' : 'w-16')}>{k}:</span>
          <span className={cn('text-foreground', multiline ? 'whitespace-pre-wrap text-xs bg-muted/50 rounded px-2 py-1 max-h-24 overflow-y-auto' : 'truncate')}>
            {v}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Execution result banner shown after approve/reject.
 */
function ExecutionResult({ action }) {
  if (action.status === 'executed') {
    const r = action.result || {};
    if (automationOutcome(r)) return <AutomationOutcomeBanner result={r} />;
    const message = AUTOMATION_ACTION_META[action.action_type]?.successMessage
      || (r.meeting_id ? 'Meeting created successfully'
      : r.task_id ? 'Task created successfully'
      : r.communication_id ? 'Email sent successfully'
      : r.survey_id ? 'Survey sent successfully'
      : r.activity_id && action.action_type === 'log_activity' ? 'Activity logged successfully'
      : r.activity_id ? 'Note added successfully'
      : r.updated_fields ? 'Task updated successfully'
      : 'Executed successfully');

    return (
      <div className="mt-1.5 flex items-center gap-1.5 rounded-md bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 px-2 py-1.5">
        <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400 shrink-0" />
        <span className="text-xs font-medium text-green-800 dark:text-green-300">{message}</span>
      </div>
    );
  }

  if (action.status === 'executing') {
    return (
      <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 dark:border-blue-800 dark:bg-blue-900/30">
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
        <span className="text-xs font-medium text-blue-800 dark:text-blue-300">Executing Action</span>
      </div>
    );
  }

  if (action.status === 'failed') {
    return (
      <div className="mt-1.5 flex items-start gap-1.5 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-2 py-1.5">
        <XCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
        <div className="text-xs text-red-800 dark:text-red-300">
          <span className="font-medium">Execution failed</span>
          {action.error && <p className="mt-0.5">{action.error}</p>}
        </div>
      </div>
    );
  }

  if (action.status === 'execution_unknown') {
    const outcomeDetail = action.result?.status === 'survey_created_email_unconfirmed'
      ? 'Survey created; email delivery could not be confirmed.'
      : action.result?.status === 'email_delivery_unconfirmed'
        ? 'Email delivery could not be confirmed.'
        : action.error;
    return (
      <div className="mt-1.5 flex items-start gap-1.5 rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-2 py-1.5">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-800 dark:text-amber-300">
          <span className="font-medium">Execution Outcome Needs Review</span>
          {outcomeDetail && <p className="mt-0.5">{outcomeDetail}</p>}
          <AutomationProgressDetails result={action.result} />
        </div>
      </div>
    );
  }

  if (action.status === 'rejected') {
    return (
      <div className="mt-1.5 flex items-center gap-1.5 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-1.5">
        <XCircle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-xs text-muted-foreground font-medium">Action rejected</span>
      </div>
    );
  }

  return null;
}

/**
 * PendingActionCard — displays a single Caleb-proposed action with approve / reject controls.
 *
 * @param {object}   props
 * @param {object}   props.action        - The pending action object from the API.
 * @param {function} props.onApprove     - Called with action.id when user clicks Approve.
 * @param {function} props.onReject      - Called with action.id when user clicks Reject.
 * @param {function} [props.onResolveExecution] - Resolve an ambiguous execution outcome.
 * @param {boolean}  [props.compact]     - Render a smaller variant (for inline chat).
 * @param {boolean}  [props.selectable]  - Show checkbox for bulk selection.
 * @param {boolean}  [props.selected]    - Whether currently selected.
 * @param {function} [props.onToggle]    - Toggle selection.
 */
const PendingActionCard = ({
  action,
  onApprove,
  onReject,
  onResolveExecution,
  compact = false,
  selectable = false,
  selected = false,
  onToggle,
}) => {
  const { hasPermission, hasAnyPermission, dataScope } = useAuth();
  const canManage = hasPermission(PERM.ASSISTANT_MANAGE);
  const canExecute = canManage && canExecutePendingAction(
    action.action_type,
    hasAnyPermission,
  );
  const canResolveExecution = canExecute && dataScope === 'all' && !!onResolveExecution;
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const meta = ACTION_TYPE_META[action.action_type] || {
    label: action.action_type,
    icon: ClipboardList,
    color: 'text-muted-foreground',
    bg: 'bg-gray-50 dark:bg-gray-800',
  };
  const Icon = meta.icon;

  const statusMeta = STATUS_VARIANT[action.status] || STATUS_VARIANT.pending;
  const isPending = action.status === 'pending';

  // Check if expired by time even if status not yet updated
  const isExpired =
    action.status === 'expired' ||
    (action.expires_at && new Date(action.expires_at) < new Date());

  const handleApprove = async (e) => {
    e.stopPropagation();
    if (!onApprove) return;
    setApproving(true);
    try {
      await onApprove(action.id);
    } catch {
      // toast is handled in the hook
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (e) => {
    e.stopPropagation();
    if (!onReject) return;
    setRejecting(true);
    try {
      await onReject(action.id);
    } catch {
      // toast is handled in the hook
    } finally {
      setRejecting(false);
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card transition-colors',
        compact ? 'px-2.5 py-2' : 'p-4',
        isExpired && 'opacity-60',
      )}
    >
      {/* Header row */}
      <div className={cn('flex items-start', compact ? 'gap-2' : 'gap-3')}>
        {canExecute && selectable && isPending && (
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggle?.(action.id)}
            className="mt-1"
            aria-label={`Select ${meta.label} action`}
          />
        )}

        {/* Icon */}
        <div className={cn('shrink-0', compact ? 'rounded-md p-1.5' : 'rounded-lg p-2', meta.bg)}>
          <Icon className={cn(compact ? 'h-3.5 w-3.5' : 'h-4 w-4', meta.color)} />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-1.5">
            <span className={cn(compact ? 'text-xs' : 'text-sm', 'font-medium text-foreground')}>{meta.label}</span>
            <div className="flex items-center gap-1.5 shrink-0">
              <Badge className={cn('text-2xs h-[18px] px-1.5', statusMeta.className)} variant={statusMeta.variant}>
                {isExpired && action.status === 'pending' ? 'Expired' : statusMeta.label}
              </Badge>
              <span className="text-2xs text-muted-foreground whitespace-nowrap leading-none">
                {timeAgo(action.created_at)}
              </span>
            </div>
          </div>

          {/* Caleb's reasoning */}
          {action.caleb_reasoning && (
            <p className={cn('text-muted-foreground line-clamp-2', compact ? 'text-xs mt-0.5 leading-tight' : 'text-xs mt-1')}>
              {action.caleb_reasoning}
            </p>
          )}

          {/* Account name */}
          {action.account_name && (
            <p className={cn('text-muted-foreground', compact ? 'text-xs mt-0.5' : 'text-xs mt-0.5')}>
              <span className="font-medium text-foreground">{action.account_name}</span>
            </p>
          )}

          {/* Parameters preview */}
          {compact ? (
            <>
              <button
                type="button"
                onClick={() => setExpanded((p) => !p)}
                className="flex items-center gap-1 mt-1 text-2xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {expanded ? 'Hide details' : 'Show details'}
              </button>
              {expanded && (
                <ActionParamsSummary actionType={action.action_type} params={action.parameters} action={action} />
              )}
            </>
          ) : (
            <>
              <ActionParamsSummary actionType={action.action_type} params={action.parameters} action={action} />
            </>
          )}

          {/* Execution result banner */}
          <ExecutionResult action={action} />

          {/* Expiry warning */}
          {isPending && !isExpired && action.expires_at && (
            (() => {
              const remaining = new Date(action.expires_at).getTime() - Date.now();
              const hoursLeft = Math.floor(remaining / 3600000);
              if (hoursLeft <= 4) {
                return (
                  <div className="flex items-center gap-1 mt-1 text-2xs text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-3 w-3" />
                    <span>Expires in {hoursLeft <= 0 ? '<1h' : `${hoursLeft}h`}</span>
                  </div>
                );
              }
              return null;
            })()
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className={cn('flex gap-1 justify-end', compact ? 'mt-1.5' : 'mt-3')}>
        {/* Review button — always available so reviewers can audit any action */}
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => { e.stopPropagation(); setPreviewOpen(true); }}
          disabled={approving || rejecting}
          className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-muted"
          title="Review details"
          aria-label="Review action details"
        >
          <Eye className="h-3.5 w-3.5" />
        </Button>
        {canManage && isPending && !isExpired && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleReject}
              disabled={rejecting || approving}
              className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
              title="Reject"
              aria-label="Reject action"
            >
              {rejecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
            </Button>
            {canExecute && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleApprove}
                disabled={approving || rejecting}
                className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/30"
                title="Approve"
                aria-label="Approve action"
              >
                {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </Button>
            )}
          </>
        )}
      </div>

      <PendingActionPreviewModal
        action={action}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        onApprove={onApprove}
        onReject={onReject}
        onResolveExecution={onResolveExecution}
        canManage={canExecute}
        canReject={canManage}
        canResolveExecution={canResolveExecution}
      />
    </div>
  );
};

export default PendingActionCard;
export { ACTION_TYPE_META, STATUS_VARIANT, canExecutePendingAction };
